import { useAppTheme } from "@hooks/useAppTheme";
import type { MixedItemTag } from "@memorang/types";
import { Box, useDevice } from "@memorang/ui";
import { ModuleActionButton } from "components/ModuleActionButton";
import { ModuleMetadataChips } from "components/ModuleMetadataChips";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Avatar, Card, Text } from "react-native-paper";
import { buttonConfig } from "../constants";
import { ModuleListItem, type ModuleListItemProps } from "./ModuleListItem";
import { ModuleThumbnail } from "./ModuleThumbnail";
import CardSkeleton from "./Skeletons/CardSkeleton";

type ModuleListItemMapper = (
	tag: MixedItemTag,
	t: TFunction,
) => ModuleListItemProps & {
	id: string;
};

type Props = {
	items: MixedItemTag[];
	loading?: boolean;
	handleSelectTopic: (id: string) => void;
	handleShowPurchase: () => void;
	getModuleListItemProps: ModuleListItemMapper;
	viewMode?: "list" | "grid";
};

type ModuleGridItemProps = ModuleListItemProps & {
	onPress: () => void;
};

type MappedModuleItem = {
	id: string;
	moduleProps: ModuleListItemProps & { id: string };
	onPress: () => void;
};

const isMappedModuleItem = (
	item: MappedModuleItem | null,
): item is MappedModuleItem => item !== null;

const chunkModuleRows = (items: MappedModuleItem[], columns: number) => {
	const rows: MappedModuleItem[][] = [];
	for (let index = 0; index < items.length; index += columns) {
		rows.push(items.slice(index, index + columns));
	}
	return rows;
};

const MODULE_GRID_CARD_HEIGHT = 168;
const MODULE_GRID_TEXT_BLOCK_HEIGHT = 76;

const ModuleGridItem = ({
	title,
	subtitle,
	status,
	cta,
	thumbnailUrl,
	thumbnailSource,
	locked,
	disabled,
	onPress,
	loading,
	duration,
	durationLabel,
	typeLabel,
	isTest,
	testKind,
	icon,
}: ModuleGridItemProps) => {
	const theme = useAppTheme();
	const { buttonMode, buttonIcon } = buttonConfig[status];
	const finalDurationLabel =
		duration !== undefined ? (durationLabel ?? `${duration} min`) : undefined;
	const metadataChipProps = isTest
		? { typeLabel }
		: { durationLabel: finalDurationLabel };

	return (
		<Card
			mode="outlined"
			onPress={disabled ? undefined : onPress}
			contentStyle={{ height: MODULE_GRID_CARD_HEIGHT }}
			style={{
				borderRadius: theme.roundness,
				backgroundColor: theme.colors.surface,
				borderColor: theme.colors.outlineVariant,
				overflow: "hidden",
				height: MODULE_GRID_CARD_HEIGHT,
			}}
		>
			<Box
				padding={16}
				flex={1}
				justifyContent="space-between"
				style={{ height: MODULE_GRID_CARD_HEIGHT, gap: 12 }}
			>
				<Box flexDirection="row" gap={12} alignItems="flex-start" flex={1}>
					<ModuleThumbnail
						size={64}
						title={title}
						thumbnailSource={thumbnailSource}
						thumbnailUrl={thumbnailUrl}
						locked={locked}
						isTest={isTest}
						testKind={testKind}
						icon={icon}
					/>
					<Box
						flex={1}
						gap={4}
						minWidth={0}
						style={{
							maxHeight: MODULE_GRID_TEXT_BLOCK_HEIGHT,
							overflow: "hidden",
						}}
					>
						<Text
							variant="titleSmall"
							numberOfLines={2}
							ellipsizeMode="tail"
							style={{
								fontWeight: "700",
								color: theme.colors.onSurface,
							}}
						>
							{title}
						</Text>
						<Text
							variant="bodySmall"
							numberOfLines={2}
							ellipsizeMode="tail"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{subtitle}
						</Text>
					</Box>
				</Box>
				<Box
					flexDirection="row"
					alignItems="flex-end"
					justifyContent="space-between"
					gap={12}
				>
					<ModuleMetadataChips
						{...metadataChipProps}
						style={{ flexShrink: 1 }}
					/>
					{locked ? (
						<Avatar.Icon
							size={32}
							icon="crown-outline"
							style={{ backgroundColor: "transparent" }}
							color={theme.colors.primary}
						/>
					) : (
						<ModuleActionButton
							mode={buttonMode}
							icon={buttonIcon}
							cta={cta}
							loading={loading}
							disabled={loading || disabled}
							onPress={onPress}
						/>
					)}
				</Box>
			</Box>
		</Card>
	);
};

export const ModulesList = ({
	items,
	loading,
	handleSelectTopic,
	handleShowPurchase,
	getModuleListItemProps,
	viewMode = "list",
}: Props) => {
	const { t } = useTranslation();
	const { isMobile } = useDevice();

	if (loading) {
		return <CardSkeleton />;
	}

	const mappedItems = items
		.map((tag) => {
			if (!("distribution" in tag)) {
				return null;
			}
			const { id, locked } = tag;
			const moduleProps = getModuleListItemProps(tag, t);
			const onPress = () => {
				if (locked) {
					handleShowPurchase();
					return;
				}
				handleSelectTopic(String(id));
			};
			return { id, moduleProps, onPress };
		})
		.filter(isMappedModuleItem);

	if (viewMode === "grid") {
		const columnCount = isMobile ? 1 : 2;
		const rows = chunkModuleRows(mappedItems, columnCount);

		return (
			<Box style={{ gap: 16 }}>
				{rows.map((row) => (
					<Box
						key={row.map((item) => item.id).join("-")}
						flexDirection={isMobile ? "column" : "row"}
						style={{ gap: 16 }}
					>
						{row.map((item) => (
							<Box key={item.id} flex={1} minWidth={0}>
								<ModuleGridItem {...item.moduleProps} onPress={item.onPress} />
							</Box>
						))}
						{row.length < columnCount
							? Array.from({ length: columnCount - row.length }).map(
									(_, index) => (
										<Box
											key={`grid-spacer-${row[0]?.id}-${index}`}
											flex={1}
											minWidth={0}
										/>
									),
								)
							: null}
					</Box>
				))}
			</Box>
		);
	}

	return (
		<Box style={{ gap: 12 }}>
			{mappedItems.map((item) => (
				<ModuleListItem
					key={item.id}
					{...item.moduleProps}
					onPress={item.onPress}
				/>
			))}
		</Box>
	);
};
